import React, { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { defaultSettings, periodOf, yen } from "../utils/payroll";

// 給与計算で「確定」した明細を、本人が閲覧するための画面。
// 下書きは見えない（Firestoreのルール側でも確定済みのものだけ読めるようにしてある）。
// 管理者が別の社員の分を見るときは targetUid を渡す。

function MyPayslips({ user, companyId, targetUid, targetName }) {
  const uid = targetUid || user.uid;
  const [rows, setRows]       = useState([]);
  const [settings, setSettings] = useState(defaultSettings(companyId));
  const [openId, setOpenId]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      try {
        const sSnap = await getDoc(doc(db, "payrollSettings", companyId));
        if (sSnap.exists()) setSettings(s => ({ ...s, ...sSnap.data() }));
      } catch { /* 設定が読めなくても明細は出す */ }

      const snap = await getDocs(query(
        collection(db, "payrolls"),
        where("uid", "==", uid),
        where("status", "==", "confirmed")
      ));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.yearMonth || "").localeCompare(a.yearMonth || ""));
      setRows(list);
      if (list.length > 0) setOpenId(list[0].id);
    } catch (e) {
      console.error("給与明細の取得に失敗しました:", e);
      setError("給与明細を読み込めませんでした。");
    } finally {
      setLoading(false);
    }
  }, [uid, companyId]);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (ts) => ts?.toDate ? ts.toDate().toLocaleDateString("ja-JP") : "";

  /** 支給日。締め日と支給月の設定から求める */
  const payDayOf = (ym) => {
    if (!ym) return "";
    const [y, m] = ym.split("-").map(Number);
    const d = new Date(y, m - 1 + (settings.payMonthOffset ?? 1), settings.payDay || 25);
    return d.toLocaleDateString("ja-JP");
  };

  const printIt = () => window.print();

  if (loading) return <p style={st.empty}>読み込み中...</p>;
  if (error)   return <p style={st.error}>{error}</p>;
  if (rows.length === 0) {
    return (
      <p style={st.empty}>
        {targetUid ? "この社員の確定済みの明細はありません" : "確定した給与明細はまだありません"}
      </p>
    );
  }

  return (
    <div>
      {targetName && <p style={st.who}>{targetName} さんの明細</p>}

      <div style={st.monthRow}>
        {rows.map(r => (
          <button key={r.id} type="button"
            style={{ ...st.monthBtn, ...(openId === r.id ? st.monthOn : {}) }}
            onClick={() => setOpenId(r.id)}>
            {r.yearMonth}
          </button>
        ))}
      </div>

      {rows.filter(r => r.id === openId).map(r => {
        const items = r.items || [];
        const earn = items.filter(i => i.type === "支給");
        const ded  = items.filter(i => i.type === "控除");
        const att  = r.attendance || {};
        const { from, to } = periodOf(r.yearMonth, settings);

        return (
          <div key={r.id} style={st.sheet}>
            <div style={st.sheetHead}>
              <div>
                <div style={st.ym}>{r.yearMonth} 給与明細</div>
                <div style={st.period}>
                  対象期間 {from.toLocaleDateString("ja-JP")}〜{to.toLocaleDateString("ja-JP")}
                  　支給日 {payDayOf(r.yearMonth)}
                </div>
              </div>
              <div style={st.person}>
                <div style={st.name}>{r.name || user.email}</div>
                {r.memberNo && <div style={st.memberNo}>{r.memberNo}</div>}
              </div>
            </div>

            <div style={st.attBox}>
              出勤 {att.workDays ?? 0}日 ／ 所定内 {att.plainHours ?? 0}h
              {att.withinOtHours > 0 && <> ／ 法定内残業 {att.withinOtHours}h</>}
              {att.overtimeHours > 0 && <> ／ 残業 {att.overtimeHours}h</>}
              {att.over60Hours > 0 && <> ／ 60時間超 {att.over60Hours}h</>}
              {att.nightHours > 0 && <> ／ 深夜 {att.nightHours}h</>}
              {att.holidayHours > 0 && <> ／ 休日 {att.holidayHours}h</>}
            </div>

            <div style={st.cols}>
              <div style={st.col}>
                <div style={st.colHead}>支給</div>
                {earn.map(i => (
                  <div key={i.key} style={st.line}>
                    <span>{i.name}</span><span style={st.num}>{yen(r.amounts?.[i.key])}</span>
                  </div>
                ))}
                <div style={st.total}><span>支給合計</span><span style={st.num}>{yen(r.totalEarnings)}</span></div>
              </div>

              <div style={st.col}>
                <div style={{ ...st.colHead, backgroundColor: "#7b4b6b" }}>控除</div>
                {ded.map(i => (
                  <div key={i.key} style={st.line}>
                    <span>{i.name}</span><span style={st.num}>{yen(r.amounts?.[i.key])}</span>
                  </div>
                ))}
                <div style={st.total}><span>控除合計</span><span style={st.num}>{yen(r.totalDeductions)}</span></div>
              </div>
            </div>

            <div style={st.net}>
              <span>差引支給額</span>
              <strong style={st.netAmt}>{yen(r.netPay)}</strong>
            </div>

            <div style={st.foot}>
              確定日 {fmtDate(r.confirmedAt)}
              {r.amendedAt && <span style={st.amended}>　※ {fmtDate(r.amendedAt)} に修正されています</span>}
            </div>

            <div style={st.btnRow}>
              <button style={st.printBtn} onClick={printIt}>印刷する</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const st = {
  empty: { color: "#999", textAlign: "center", marginTop: "32px" },
  error: { color: "#e74c3c", fontSize: "14px" },
  who: { fontSize: "14px", color: "#2c3e50", fontWeight: "bold", marginTop: 0 },
  monthRow: { display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "14px" },
  monthBtn: { padding: "7px 14px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "13px", color: "#7f8c8d" },
  monthOn: { border: "2px solid #2c3e50", backgroundColor: "#2c3e50", color: "#fff", fontWeight: "bold" },
  sheet: { backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", padding: "24px 26px" },
  sheetHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap", borderBottom: "2px solid #f0f4f8", paddingBottom: "12px", marginBottom: "14px" },
  ym: { fontSize: "19px", fontWeight: "bold", color: "#2c3e50" },
  period: { fontSize: "12px", color: "#7f8c8d", marginTop: "4px" },
  person: { textAlign: "right" },
  name: { fontSize: "15px", fontWeight: "bold", color: "#2c3e50" },
  memberNo: { fontSize: "11px", color: "#2980b9", fontFamily: "monospace" },
  attBox: { fontSize: "13px", color: "#555", backgroundColor: "#f7f9fb", borderRadius: "8px", padding: "10px 14px", marginBottom: "16px", lineHeight: "1.8" },
  cols: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "16px" },
  col: { border: "1px solid #eef2f6", borderRadius: "10px", overflow: "hidden" },
  colHead: { backgroundColor: "#1f6f8b", color: "#fff", padding: "8px 14px", fontSize: "13px", fontWeight: "bold" },
  line: { display: "flex", justifyContent: "space-between", padding: "8px 14px", borderBottom: "1px solid #f4f6f8", fontSize: "14px", color: "#2c3e50" },
  total: { display: "flex", justifyContent: "space-between", padding: "10px 14px", backgroundColor: "#f7f9fb", fontSize: "14px", fontWeight: "bold", color: "#2c3e50" },
  num: { fontFamily: "monospace" },
  net: { display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#eef7f1", border: "1px solid #a9dfbf", borderRadius: "10px", padding: "14px 18px", marginTop: "16px", fontSize: "15px", color: "#1e7e46" },
  netAmt: { fontSize: "22px", fontFamily: "monospace" },
  foot: { fontSize: "12px", color: "#95a5a6", marginTop: "12px" },
  amended: { color: "#e67e22" },
  btnRow: { display: "flex", gap: "10px", marginTop: "16px" },
  printBtn: { padding: "9px 20px", backgroundColor: "#fff", color: "#2c3e50", border: "1px solid #2c3e50", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
};

export default MyPayslips;
